import React, { useReducer, createContext, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { gameReducer, gameInitialState } from "../reducers";
import constant from "../constants/gameConstant";
import { gameService } from "../services";
import { toast } from "react-toastify";
import { history } from "../tools";

export const GameContext = createContext({
    state: gameInitialState,
    actions: {
        get: () => { },
        add: () => { },
        should: () => { },
        addPlayList: () => { },
        getPlatforms: () => { },
        search: () => { },
        paginate: () => { },
        reset: () => { },
        goToDetail: () => { }
    }
});

export const GameContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(gameReducer, gameInitialState);

    const get = useCallback((id, isRequest = true) => {
        if (isRequest) dispatch({ type: constant.GET_REQUEST });

        gameService
            .get(id)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.GET_FAIL });
                    return;
                }
                dispatch({ type: constant.GET_SUCCESS, payload: response.data });
            })
            .catch(() => {
                toast.error("the game couldn't be fetched!");
                dispatch({ type: constant.GET_FAIL });
            });
    }, []);

    const add = useCallback((game, onSuccess) => {
        dispatch({ type: constant.ADD_REQUEST });

        gameService
            .add(game)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.ADD_FAIL });
                    return;
                }
                dispatch({ type: constant.ADD_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess(response.data);
            })
            .catch(() => {
                toast.error("the game couldn't be added!");
                dispatch({ type: constant.ADD_FAIL });
            });
    }, []);

    const should = useCallback((dto, onSuccess) => {
        dispatch({ type: constant.SHOULD_REQUEST });

        gameService
            .should(dto)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.SHOULD_FAIL });
                    return;
                }
                dispatch({ type: constant.SHOULD_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("the game couldn't be rated!");
                dispatch({ type: constant.SHOULD_FAIL });
            });
    }, []);

    const addPlayList = useCallback((dto, onSuccess) => {
        dispatch({ type: constant.ADD_LIST_REQUEST });

        gameService
            .addPlayList(dto)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.ADD_LIST_FAIL });
                    return;
                }
                dispatch({ type: constant.ADD_LIST_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("The game couldn't be added to your playing list!");
                dispatch({ type: constant.ADD_LIST_FAIL });
            });
    }, []);

    const getPlatforms = useCallback(() => {
        dispatch({ type: constant.GET_PLATFORMS_REQUEST });

        gameService
            .getPlatforms()
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.GET_PLATFORMS_FAIL });
                    return;
                }
                dispatch({ type: constant.GET_PLATFORMS_SUCCESS, payload: response.data });
            })
            .catch(() => {
                toast.error("platforms couldn't be fetched!");
                dispatch({ type: constant.GET_PLATFORMS_FAIL });
            });
    }, []);

    const search = useCallback((key) => {
        dispatch({ type: constant.SEARCH_REQUEST });

        gameService
            .search(key)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.SEARCH_FAIL });
                    return;
                }
                dispatch({ type: constant.SEARCH_SUCCESS, payload: response.data });
            })
            .catch(() => {
                toast.error("games couldn't be fetched!");
                dispatch({ type: constant.SEARCH_FAIL });
            });
    }, []);

    const paginate = useCallback((dto, onSuccess) => {
        const payload = { limit: 12, ...dto };
        dispatch({ type: constant.PAGINATE_REQUEST });

        gameService
            .paginate(payload)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.PAGINATE_FAIL });
                    return;
                }
                dispatch({ type: constant.PAGINATE_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("games couldn't be fetched!");
                dispatch({ type: constant.PAGINATE_FAIL });
            });
    }, []);

    const goToDetail = useCallback((id) => {
        history.push(`/game/${id}`);
    }, []);

    const reset = useCallback(() => {
        dispatch({ type: constant.RESET });
    }, []);

    const actions = useMemo(
        () => ({
            add,
            search,
            paginate,
            getPlatforms,
            get,
            should,
            reset,
            addPlayList,
            goToDetail
        }),
        [add, search, paginate, getPlatforms, get, should, reset, addPlayList, goToDetail]
    );

    const value = useMemo(() => ({ state, actions }), [state, actions]);

    return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

GameContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};
